import React, { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);

import { BidSubmittedButton } from
  'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/AuctionItemActions.style';
import { CatalogTextAlerts } from
  'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogTextAlerts/CatalogTextAlerts';
import { CatalogWishlists } from
  'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/CatalogWishlists/CatalogWishlists';
import { PlaceBid } from 'src/components/PlaceBid/PlaceBid';
import { ShareBar } from '@riac/design-system';
import { formatCurrency } from 'src/lib/catalog';
import { LoginContext } from 'src/lib/LoginContext';
import { IDetailPageActionArea } from 'src/pages/DetailPage/components/DetailPageActionArea.i';
import { DetailPageActionAreaActions, DetailPageBidButtons, DetailPageShareContainer } from
  'src/pages/DetailPage/components/DetailPageActionArea.style';
import { DetailPageEstimatedPrice } from 'src/pages/DetailPage/DetailPage.style';
import { LoginToUse } from 'src/components/LoginToUse/LoginToUse';
import { BiddingContext } from 'src/lib/BiddingContext';
import { getIsPricelessLot } from 'src/lib/getIsPricelessLot';
import { getEndMessage } from 'src/components/AuctionInfo/TimedAuctionInfo';
import {
  getBidCountMessage,
} from 'src/pages/CatalogPage/components/AuctionItems/AuctionItem/AuctionItemActions/AuctionItemActions';

export const DetailPageActionArea = ({
  auctionId, itemInfo, primaryItem, hideShare,
}: IDetailPageActionArea): JSX.Element => {
  const endDate = dayjs.tz(primaryItem.EndDate, 'America/Chicago');
  const now = dayjs().tz('America/Chicago');
  const { isLoggedIn } = useContext(LoginContext);
  const [bidInCart, setBidInCart] = useState(false);
  const [timedAuctionLotActive, setTimedAuctionLotActive] = useState(endDate.isAfter(now));
  const { getMessageForLot } = useContext(BiddingContext);
  useEffect(() => {
    setBidInCart(false);
    const e = dayjs.tz(primaryItem.EndDate, 'America/Chicago');
    const n = dayjs().tz('America/Chicago');
    const diff = e.diff(n);
    let timer = null;
    if(e.isAfter(n) && diff > 0 && diff < 86400000) {
      setTimedAuctionLotActive(true);
      timer = setTimeout(() => {
        setTimedAuctionLotActive(false);
      }, diff+1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [primaryItem.EndDate]);

  return (
    <DetailPageShareContainer>
      {!hideShare
        && <ShareBar
          title={primaryItem.Headline}
          text='Share this item:'
          image={`${window.CDN_PATH}${auctionId}/${itemInfo.Photos[0]}`}
        />
      || ''}
      {(itemInfo?.AuctionTypeID !== TIMED_AUCTION_TYPE_ID)
      && <DetailPageEstimatedPrice>
        Estimated Price: {
          getIsPricelessLot(primaryItem?.AuctionID, primaryItem?.LotNumber) ? 'Priceless'
            : (
              (primaryItem?.LowEst && primaryItem?.HighEst)
                ? `$${primaryItem?.LowEst.toLocaleString()} - $${primaryItem?.HighEst.toLocaleString()}`
                : 'Not Available'
            )
        }
      </DetailPageEstimatedPrice>
      }
      <DetailPageActionAreaActions>
        <LoginToUse
          overlayText = 'Log In to Place Bids or Create Wishlists and Text Alerts'
        >
          {itemInfo.AuctionTypeID === TIMED_AUCTION_TYPE_ID
          && <div>
            {primaryItem.CurrentBid && <>
            Current Bid: {formatCurrency(primaryItem.CurrentBid)} {getBidCountMessage(primaryItem.StartingBid, primaryItem.CurrentBid)}
            </> || <>
                Starting Bid: {formatCurrency(primaryItem.StartingBid)}
            </>}
            <br/>
            <>
              {getEndMessage(now, endDate)}
              <br/>
              {(primaryItem.HighBidder && (
                <span style={{ color: 'rgba(40, 220, 40, 1)', fontWeight: 600 }}>
                    You are the high bidder<br />
                    Max Bid: {formatCurrency(primaryItem.HighBidAmount)}
                </span>
              )) || ((primaryItem.PreviousBid && isLoggedIn)
                  && <span style={{ color: 'rgba(220, 30, 30, 1)', fontWeight: 600 }}>
                    You are out bid
                  </span>
                || '')}
            </>
            <br/>
          </div>
          }
          {(primaryItem.citem === 1 || bidInCart) && (
            <>
              <BidSubmittedButton to='/account/cart' title='View Your Bid Cart'>
              In Cart
              </BidSubmittedButton>
              <br />
            </>
          )}
          {itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID && primaryItem.sbitem === 1 && (
            <BidSubmittedButton to='/account/bids' title='View All Submitted Bids'>
            Bid Submitted
            </BidSubmittedButton>
          )}
          <DetailPageBidButtons>
            {getMessageForLot(itemInfo.LotNumber)}
            {(itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID || timedAuctionLotActive) && (
              <PlaceBid
                type='sealed'
                page='detail'
                auctionTypeID={itemInfo.AuctionTypeID}
                item={primaryItem}
                addBid={() => {setBidInCart(true);}}
              />
            )}
            {(itemInfo.AuctionTypeID === TIMED_AUCTION_TYPE_ID && timedAuctionLotActive) && (
              <PlaceBid
                type={'increment'}
                page={'catalog'}
                item={primaryItem}
                auctionTypeID={itemInfo.AuctionTypeID}
                addBid={() => {}}
              />
            )}
            {itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID && (
              <PlaceBid
                type='phone'
                page='detail'
                auctionTypeID={itemInfo.AuctionTypeID}
                item={primaryItem}
                addBid={() => setBidInCart(true)}
              />
            )}
          </DetailPageBidButtons>
          <div>
            <CatalogWishlists
              item={primaryItem}
              title={itemInfo.Title}
              backgroundColor={'#1f1f1f'}
            />
            {itemInfo.AuctionTypeID !== TIMED_AUCTION_TYPE_ID && (
              <CatalogTextAlerts
                item={primaryItem}
              />
            )}
          </div>
        </LoginToUse>
      </DetailPageActionAreaActions>
    </DetailPageShareContainer>
  );
};
